import React from 'react';
import { Calendar, dateFnsLocalizer, type View } from 'react-big-calendar';
import { format, parse, startOfWeek, getDay } from 'date-fns';
import { fr } from 'date-fns/locale';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import type { AgendaItem } from '../types';

type TFn = (key: string, vars?: Record<string, string | number | boolean | null | undefined>, fallback?: string) => string;

const localizer = dateFnsLocalizer({
    format,
    parse,
    startOfWeek: () => startOfWeek(new Date(), { locale: fr }),
    getDay,
    locales: { fr },
});

interface AgendaCalendarEvent {
    id: number;
    title: string;
    start: Date;
    end: Date;
    url: string;
    type: string;
}

function toDate(value: string): Date | null {
    if (!value) {
        return null;
    }
    const d = new Date(value.replace(' ', 'T'));
    return Number.isNaN(d.getTime()) ? null : d;
}

interface Props {
    items: AgendaItem[];
    t: TFn;
}

/** Vue calendrier mensuel (react-big-calendar). Clic sur un événement → fiche activité. */
export function AgendaCalendarView({ items, t }: Props): React.ReactElement {
    const [view, setView] = React.useState<View>('month');
    const [date, setDate] = React.useState<Date>(new Date());

    const events = React.useMemo<AgendaCalendarEvent[]>(() => {
        const out: AgendaCalendarEvent[] = [];
        items.forEach((it) => {
            const start = toDate(it.date_start);
            if (!start) {
                return;
            }
            const end = toDate(it.date_end) ?? start;
            out.push({ id: it.id, title: it.title, start, end, url: it.url, type: it.type });
        });
        return out;
    }, [items]);

    const messages = React.useMemo(() => ({
        today: t('agenda.calendar.today', {}, "Aujourd'hui"),
        // Flèches plutôt que « Précédent / Suivant » (plus compact).
        previous: '‹',
        next: '›',
        month: t('agenda.calendar.month', {}, 'Mois'),
        week: t('agenda.calendar.week', {}, 'Semaine'),
        day: t('agenda.calendar.day', {}, 'Jour'),
        agenda: t('agenda.calendar.agenda', {}, 'Liste'),
        date: t('agenda.calendar.date', {}, 'Date'),
        time: t('agenda.calendar.time', {}, 'Heure'),
        event: t('agenda.calendar.event', {}, 'Activité'),
        noEventsInRange: t('agenda.calendar.no_events', {}, 'Aucune activité sur cette période.'),
        showMore: (count: number) => t('agenda.calendar.show_more', { count }, `+ ${count}`),
    }), [t]);

    return (
        <div className="gas-agenda-calendar">
            <Calendar
                localizer={localizer}
                events={events}
                startAccessor="start"
                endAccessor="end"
                culture="fr"
                style={{ height: 640 }}
                view={view}
                onView={setView}
                date={date}
                onNavigate={setDate}
                views={['month', 'week', 'agenda']}
                popup
                messages={messages}
                onSelectEvent={(event: AgendaCalendarEvent) => {
                    if (event.url) {
                        window.location.href = event.url;
                    }
                }}
            />
        </div>
    );
}
